import type { ReactNode } from "react";

/* ---------------------------------------------------------------------------
   EXPERIMENTAL styling. Everything visual that we are still trying out lives
   here so it can be reverted by deleting this file and its handful of usages
   (search for "@/components/experimental/aura"). The matching CSS lives in
   globals.css under the "Experimental styling" heading.
--------------------------------------------------------------------------- */

/**
 * Drifting ribbons of light behind a page. Purely decorative: a stack of
 * stroked sine curves sharing one brand gradient, which is what produces the
 * moiré shimmer without any animation cost.
 */
export function FlowLines({
  className = "",
  bands = 1,
  opacity = 0.85,
  anchor = "fill",
}: {
  className?: string;
  bands?: number;
  opacity?: number;
  /**
   * "fill" stretches to the host box — right for a fixed-height pane like the
   * sign-in column. "viewport" pins a band to the bottom of the screen, which
   * is what long scrolling pages need: filling those would stretch every curve
   * over the whole document height.
   */
  anchor?: "fill" | "viewport";
}) {
  /* A band is a tight cluster of near-parallel curves — that close spacing is
     what makes the moiré shimmer. One band reads as a single wave; more than
     that and the crossings turn into visual noise. */
  const paths = Array.from({ length: bands }, (_, band) => {
    const baseY = 700 - band * 150;
    const lift = 150 + band * 40;
    const phase = band % 2 === 0 ? 1 : -1;
    return Array.from({ length: 18 }, (_, line) => {
      const y = baseY + line * 5;
      return `M-40,${y} C300,${y - lift * phase} 620,${y + lift * phase} 900,${y - 30 * phase} S1300,${y + lift * 0.7 * phase} 1480,${y - 40 * phase}`;
    });
  }).flat();

  const viewport = anchor === "viewport";
  const shell = viewport ? "fixed inset-x-0 bottom-0 h-[26rem]" : "absolute inset-0";

  /* Fade the strokes out at every edge. Without this the band ends on a hard
     line and reads as a pasted-in image rather than part of the page. */
  const fade = [
    "linear-gradient(to bottom, transparent 2%, #000 40%, #000 88%, transparent 100%)",
    "linear-gradient(to right, transparent 0%, #000 14%, #000 86%, transparent 100%)",
  ].join(", ");

  return (
    <div
      className={`pointer-events-none ${shell} z-0 overflow-hidden ${className}`}
      aria-hidden
    >
      {/* Colour blooms only where the host box is tall enough to absorb them.
          Over a short band they tint a visible rectangle instead. */}
      {!viewport && (
        <>
          <div className="absolute -left-24 bottom-0 h-[26rem] w-[26rem] rounded-full bg-[var(--brand-accent)] opacity-[0.16] blur-[120px]" />
          <div className="absolute -right-20 bottom-0 h-[30rem] w-[30rem] rounded-full bg-[var(--brand-secondary)] opacity-[0.16] blur-[130px]" />
        </>
      )}

      <svg
        viewBox={viewport ? "0 420 1440 480" : "0 0 1440 900"}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        style={{
          opacity,
          maskImage: fade,
          WebkitMaskImage: fade,
          maskComposite: "intersect",
          WebkitMaskComposite: "source-in",
        }}
      >
        <defs>
          <linearGradient id="aura-flow" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--brand-accent)" />
            <stop offset="45%" stopColor="var(--brand-primary)" />
            <stop offset="100%" stopColor="var(--brand-secondary)" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#aura-flow)" strokeWidth="1.15">
          {paths.map((d, i) => (
            <path key={i} d={d} opacity={0.6 + (i % 5) * 0.08} />
          ))}
        </g>
      </svg>
    </div>
  );
}

/**
 * An outline icon inside a glowing gradient ring — the treatment used for
 * empty states, where a flat grey circle reads as "nothing here" rather than
 * "something is coming".
 */
export function AuraIcon({
  children,
  size = "md",
}: {
  children: ReactNode;
  size?: "sm" | "md" | "lg";
}) {
  const box =
    size === "lg" ? "h-20 w-20" : size === "sm" ? "h-11 w-11" : "h-14 w-14";

  return (
    <span className={`aura-icon relative inline-flex ${box} items-center justify-center`}>
      <span aria-hidden className="aura-icon__glow" />
      <span aria-hidden className="aura-icon__ring" />
      <span className="relative text-[var(--brand-primary)]">{children}</span>
    </span>
  );
}
